import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Autocomplete,
  Icon,
  Text,
  Box,
  Spinner,
  InlineStack,
  Button,
  BlockStack,
  Collapsible,
} from "@medentic/polaris-react";
import { SearchIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { GeoAddress, GeoAddressType, MultilingualText } from "@features/events/types";
import MapPicker from "./MapPicker";
import { GoogleMapsProvider } from "./GoogleMapsProvider";
import GenerativeTextField from "../GenerativeTextField/GenerativeTextField";

// Re-export GeoAddress for convenience
export type { GeoAddress };

type AddressPickerProps = {
  value?: GeoAddress | null;
  onChange: (geoAddress: GeoAddress | null) => void;
  error?: string;
  disabled?: boolean;
  requiredIndicator?: boolean;
};

interface GeoSearchResult {
  placeId: string;
  text: string;
}

interface AutocompleteSection {
  title: string;
  options: Array<{ value: string; label: string }>;
}

// Generate or retrieve session token for Google Places API
const getGeoSessionToken = (): string => {
  const SESSION_KEY = "geo_session_token2";
  let sessionToken = sessionStorage.getItem(SESSION_KEY);

  if (!sessionToken) {
    sessionToken = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sessionToken);
  }

  return sessionToken;
};

export default function AddressPicker({
  value,
  onChange,
  error,
  disabled = false,
  requiredIndicator = false,
}: AddressPickerProps) {
  const { GET } = useApiRequest();
  const { t, i18n } = useTranslation();

  const [inputValue, setInputValue] = useState(value?.summary?.[i18n.language] || value?.summary?.en || "");
  const [searchResults, setSearchResults] = useState<GeoSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const [isOverrideOpen, setIsOverrideOpen] = useState(false);

  // Override mode state
  const [overrideAddress1, setOverrideAddress1] = useState<MultilingualText>({});
  const [overrideCity, setOverrideCity] = useState<MultilingualText>({});
  const [overrideCoords, setOverrideCoords] = useState<{ lat: number; lng: number } | null>(null);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousPlaceIdRef = useRef<string | undefined>(value?.googlePlaceId);

  // Sync override fields with API data when address changes
  // This ensures the hidden fields (address1, city) are auto-filled with API data
  useEffect(() => {
    if (value) {
      // Update override fields with data from the API
      setOverrideAddress1(value.address1 || {});
      setOverrideCity(value.city || {});
      if (value.latitude != null && value.longitude != null) {
        setOverrideCoords({ lat: value.latitude, lng: value.longitude });
      } else {
        setOverrideCoords(null);
      }
    } else {
      // Reset when value is cleared
      setOverrideAddress1({});
      setOverrideCity({});
      setOverrideCoords(null);
    }
  }, [value]);

  // Fetch full place details from the API
  const fetchPlaceDetails = useCallback(
    async (placeId: string): Promise<GeoAddress | null> => {
      setLoadingDetails(true);
      console.log(`[AddressPicker] Fetching place details for: ${placeId}`);
      
      try {
        // GET function in baseAPI already unwraps response.data.data
        // API returns GeoAddress (AddressDTO) directly
        const placeData = await GET<GeoAddress>(
          `/api/metadata/geo/places/${placeId}`,
          false,
          false,
          true
        );
        
        // placeData is already a GeoAddress, just validate and return
        if (placeData && placeData.googlePlaceId) {
          return placeData;
        }
        
        console.warn("[AddressPicker] Invalid response - missing googlePlaceId. Response:", placeData);
        return null;
      } catch (error) {
        console.error("[AddressPicker] API call failed:", error);
        return null;
      } finally {
        setLoadingDetails(false);
      }
    },
    [GET]
  );

  const searchLocations = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim() || searchTerm.length < 3) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      try {
        const sessionToken = getGeoSessionToken();
        
        const queryParams = {
          address: searchTerm,
          sessionToken: sessionToken,
          limit: 10,
        };

        const queryString = new URLSearchParams(
          Object.entries(queryParams).reduce((acc, [key, value]) => {
            acc[key] = String(value);
            return acc;
          }, {} as Record<string, string>)
        ).toString();

        const endpoint = `/api/metadata/geo/addresses?${queryString}`;

        const results = await GET<GeoSearchResult[]>(
          endpoint,
          false,
          false,
          true
        );

        const locations = results || [];
        setSearchResults(locations);
      } catch (error) {
        console.error("Address search failed:", error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    },
    [GET]
  );

  const handleInputChange = useCallback(
    (searchValue: string) => {
      setInputValue(searchValue);
      setHasUserTyped(true);

      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

      // Only clear the value if the field is completely empty
      if (!searchValue.trim()) {
        setSearchResults([]);
        onChange(null);
        setHasUserTyped(false);
        return;
      }

      // If user is typing but hasn't reached 3 characters yet, just clear search results
      // but keep the existing address value (so map stays visible)
      if (searchValue.trim().length < 3) {
        setSearchResults([]);
        return;
      }

      // Start searching after 3+ characters
      searchTimeoutRef.current = setTimeout(() => {
        searchLocations(searchValue);
      }, 500);
    },
    [searchLocations, onChange]
  );

  const options = useMemo((): AutocompleteSection[] => {
    if (searchResults.length === 0) {
      return [];
    }

    return [
      {
        title: "",
        options: searchResults.map((location) => ({
          value: location.placeId,
          label: location.text,
        })),
      },
    ];
  }, [searchResults]);

  const updateSelection = useCallback(
    async (selected: string[]) => {
      if (selected.length === 0) {
        setSelectedOptions([]);
        setInputValue("");
        onChange(null);
        return;
      }

      const selectedPlaceId = selected[0];
      const selectedLocation = searchResults.find(
        (location) => location.placeId === selectedPlaceId
      );

      if (selectedLocation) {
        console.log("[AddressPicker] Address selected:", selectedLocation.text);
        setSelectedOptions(selected);
        setInputValue(selectedLocation.text);
        setHasUserTyped(false);

        // Fetch full place details from API
        const fullPlaceData = await fetchPlaceDetails(selectedPlaceId);
        
        if (fullPlaceData) {
          console.log("[AddressPicker] Full place data fetched successfully");
          onChange(fullPlaceData);
        } else {
          // Fallback to basic data if fetch fails (without coordinates)
          console.warn("[AddressPicker] Using fallback data (API call failed)");
          onChange({
            summary: { [i18n.language]: selectedLocation.text },
            googlePlaceId: selectedLocation.placeId,
            city: {},
            country: {},
            countryISO3: "",
            type: GeoAddressType.ADDRESS,
            latitude: null,
            longitude: null,
          });
        }
      }
    },
    [searchResults, onChange, fetchPlaceDetails, i18n.language]
  );

  // Update display value when data changes externally
  useEffect(() => {
    if (!hasUserTyped && value) {
      const displayValue = value.summary?.[i18n.language] || value.summary?.en || "";
      setInputValue(displayValue);
    }
  }, [value, i18n.language, hasUserTyped]);

  // Reset when placeId changes externally
  useEffect(() => {
    const currentPlaceId = value?.googlePlaceId;
    const previousPlaceId = previousPlaceIdRef.current;

    if (currentPlaceId !== previousPlaceId) {
      previousPlaceIdRef.current = currentPlaceId;
    }
  }, [value?.googlePlaceId]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleOverrideToggle = useCallback(() => {
    setIsOverrideOpen(!isOverrideOpen);
  }, [isOverrideOpen]);

  // Handle map coordinate changes - update immediately
  const handleMapCoordinatesChange = useCallback(
    (lat: number, lng: number) => {
      setOverrideCoords({ lat, lng });
      
      // Update the parent immediately with new coordinates
      if (value) {
        const updatedValue: GeoAddress = {
          ...value,
          latitude: lat,
          longitude: lng,
        };
        onChange(updatedValue);
      }
    },
    [value, onChange]
  );

  // Handle address1 changes - update immediately
  const handleAddress1Change = useCallback(
    (newAddress1: MultilingualText) => {
      setOverrideAddress1(newAddress1);
      
      // Update the parent immediately
      if (value) {
        const updatedValue: GeoAddress = {
          ...value,
          address1: newAddress1,
        };
        onChange(updatedValue);
      }
    },
    [value, onChange]
  );

  // Handle city changes - update immediately
  const handleCityChange = useCallback(
    (newCity: MultilingualText) => {
      setOverrideCity(newCity);
      
      // Update the parent immediately
      if (value) {
        const updatedValue: GeoAddress = {
          ...value,
          city: newCity,
        };
        onChange(updatedValue);
      }
    },
    [value, onChange]
  );

  const textField = useMemo(
    () => (
      <Autocomplete.TextField
        onChange={handleInputChange}
        label={t('events.component.address_picker.address')}
        value={inputValue}
        prefix={<Icon source={SearchIcon} />}
        placeholder={t('events.component.address_picker.placeholder_search_address')}
        autoComplete="off"
        error={error}
        disabled={disabled}
        requiredIndicator={requiredIndicator}
      />
    ),
    [handleInputChange, inputValue, error, disabled, requiredIndicator, t]
  );

  const emptyState = useMemo(
    () =>
      loading ? (
        <Box padding="400">
          <InlineStack align="center" blockAlign="center" gap="200">
            <Spinner size="small" />
            <Text as="span" variant="bodyMd" tone="subdued">
              {t('events.component.address_picker.searching_address')}
            </Text>
          </InlineStack>
        </Box>
      ) : inputValue.trim().length > 0 && inputValue.trim().length < 3 ? (
        <Box padding="400">
          <Text as="span" variant="bodyMd" tone="subdued">
            {t('events.component.address_picker.type_more_characters')}
          </Text>
        </Box>
      ) : (
        <Box padding="400">
          <Text as="span" variant="bodyMd" tone="subdued">
            {t('events.component.address_picker.no_resuls_found')}
          </Text>
        </Box>
      ),
    [loading, inputValue, t]
  );

  return (
    <BlockStack gap="300">
      <Autocomplete
        options={options}
        selected={selectedOptions}
        onSelect={updateSelection}
        textField={textField}
        emptyState={emptyState}
        listTitle={
          searchResults.length > 0
            ? t('events.component.address_picker.addresses')
            : undefined
        }
      />

      {loadingDetails && (
        <InlineStack align="center" gap="200">
          <Spinner size="small" />
          <Text as="span" variant="bodyMd" tone="subdued">
            {t('events.component.address_picker.loading_details', 'Loading address details...')}
          </Text>
        </InlineStack>
      )}

      {value && value.latitude != null && value.longitude != null && !loadingDetails && (
        <BlockStack gap="400">
          {/* Map - always visible when address is selected */}
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd" fontWeight="medium">
              {t('events.component.address_picker.location_on_map', 'Location on Map')}
            </Text>
            <GoogleMapsProvider>
              <MapPicker
                latitude={overrideCoords?.lat ?? value.latitude!}
                longitude={overrideCoords?.lng ?? value.longitude!}
                onCoordinatesChange={handleMapCoordinatesChange}
              />
            </GoogleMapsProvider>
          </BlockStack>

          {/* Override section for manual field editing */}
          <Box>
            <Button 
              variant="plain" 
              onClick={handleOverrideToggle}
              disclosure={isOverrideOpen ? "up" : "down"}
            >
              {t('events.component.address_picker.override', 'Override Address Details')}
            </Button>

            <Collapsible
              open={isOverrideOpen}
              id="address-override-collapsible"
              transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
            >
              <Box paddingBlockStart="400">
                <BlockStack gap="400">
                  {/* Address1 multilingual fields */}
                  <GenerativeTextField
                    id="address-picker-address1"
                    placementId="address-picker-street-address"
                    value={overrideAddress1}
                    onChange={handleAddress1Change}
                    type="textField"
                    label={t('events.component.address_picker.street_address', 'Street Address')}
                    canGenerate={false}
                  />

                  {/* City multilingual fields */}
                  <GenerativeTextField
                    id="address-picker-city"
                    placementId="address-picker-city"
                    value={overrideCity}
                    onChange={handleCityChange}
                    type="textField"
                    label={t('events.component.address_picker.city', 'City')}
                    canGenerate={false}
                  />
                </BlockStack>
              </Box>
            </Collapsible>
          </Box>
        </BlockStack>
      )}
    </BlockStack>
  );
}
